import { useState } from 'react';
import { Check, ChevronDown, Eye, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import type { AiApplicationResponse, AiSuggestion } from '@/lib/api';

interface ArtifactAiSuggestionListProps {
  suggestions: AiSuggestion[];
  recentlyResolvedSuggestions: AiSuggestion[];
  loading: boolean;
  disabled: boolean;
  applyingSuggestionId: string | null;
  rejectingSuggestionId: string | null;
  /** Currently previewed suggestion id, if any. */
  previewSuggestionId?: string | null;
  /** Open the suggestion in the main document area as a draft preview. */
  onOpenPreview?: (suggestionId: string) => void;
  onApply: (suggestionId: string) => Promise<AiApplicationResponse>;
  onReject: (suggestionId: string, reason?: string) => Promise<AiSuggestion>;
}

export function ArtifactAiSuggestionList({
  suggestions,
  recentlyResolvedSuggestions,
  loading,
  disabled,
  applyingSuggestionId,
  rejectingSuggestionId,
  previewSuggestionId = null,
  onOpenPreview,
  onApply,
  onReject,
}: ArtifactAiSuggestionListProps): React.ReactElement {
  const [resolvedOpen, setResolvedOpen] = useState(false);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-text-primary">Open suggestions</h3>
        {loading && <span className="text-xs text-text-secondary">Loading…</span>}
      </div>
      {suggestions.length === 0 ? (
        <div className="rounded-md border border-border bg-surface/40 p-3 text-xs text-text-secondary">
          No open suggestions.
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {suggestions.map(suggestion => (
            <SuggestionCard
              key={suggestion.id}
              suggestion={suggestion}
              disabled={disabled}
              applying={applyingSuggestionId === suggestion.id}
              rejecting={rejectingSuggestionId === suggestion.id}
              previewing={previewSuggestionId === suggestion.id}
              onOpenPreview={onOpenPreview}
              onApply={onApply}
              onReject={onReject}
            />
          ))}
        </ul>
      )}
      <Collapsible open={resolvedOpen} onOpenChange={setResolvedOpen}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex w-fit items-center gap-1 text-xs font-medium text-text-secondary hover:text-text-primary"
          >
            <ChevronDown
              className={resolvedOpen ? 'size-4 rotate-180 transition-transform' : 'size-4'}
            />
            Recently resolved
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          {recentlyResolvedSuggestions.length === 0 ? (
            <div className="mt-2 rounded-md border border-border bg-surface/40 p-3 text-xs text-text-secondary">
              No recently resolved suggestions.
            </div>
          ) : (
            <ul className="mt-2 flex flex-col gap-2">
              {recentlyResolvedSuggestions.map(suggestion => (
                <ResolvedSuggestionCard key={suggestion.id} suggestion={suggestion} />
              ))}
            </ul>
          )}
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

function SuggestionCard({
  suggestion,
  disabled,
  applying,
  rejecting,
  previewing,
  onOpenPreview,
  onApply,
  onReject,
}: {
  suggestion: AiSuggestion;
  disabled: boolean;
  applying: boolean;
  rejecting: boolean;
  previewing: boolean;
  onOpenPreview?: (suggestionId: string) => void;
  onApply: (suggestionId: string) => Promise<AiApplicationResponse>;
  onReject: (suggestionId: string, reason?: string) => Promise<AiSuggestion>;
}): React.ReactElement {
  const busy = applying || rejecting;
  return (
    <li className="rounded-md border border-border bg-surface/40 p-3">
      <div className="flex flex-col gap-3">
        <SuggestionHeader suggestion={suggestion} />
        <div className="truncate rounded-md bg-background/60 px-2 py-1 font-mono text-xs text-success">
          {markdownDiffPreview(suggestion)}
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          {onOpenPreview && (
            <Button
              type="button"
              size="sm"
              disabled={disabled || busy}
              onClick={() => {
                onOpenPreview(suggestion.id);
              }}
              className="inline-flex gap-1.5"
              aria-pressed={previewing}
            >
              <Eye className="size-4" />
              {previewing ? 'Previewing' : 'Open'}
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            disabled={disabled || busy}
            onClick={() => void onReject(suggestion.id).catch(() => undefined)}
            variant="ghost"
            className="inline-flex gap-1.5"
          >
            <X className="size-4" />
            {rejecting ? 'Rejecting…' : 'Reject'}
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={disabled || busy}
            onClick={() => void onApply(suggestion.id).catch(() => undefined)}
            variant="outline"
            className="inline-flex gap-1.5"
          >
            <Check className="size-4" />
            {applying ? 'Applying…' : 'Accept'}
          </Button>
        </div>
      </div>
    </li>
  );
}

function ResolvedSuggestionCard({ suggestion }: { suggestion: AiSuggestion }): React.ReactElement {
  return (
    <li className="rounded-md border border-border bg-surface/30 p-3 opacity-80">
      <div className="flex flex-col gap-2">
        <SuggestionHeader suggestion={suggestion} />
        <div className="truncate rounded-md bg-background/60 px-2 py-1 font-mono text-xs text-text-secondary">
          {markdownDiffPreview(suggestion)}
        </div>
      </div>
    </li>
  );
}

function SuggestionHeader({ suggestion }: { suggestion: AiSuggestion }): React.ReactElement {
  return (
    <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
      <div className="min-w-0">
        <div className="truncate text-sm text-text-primary">
          {suggestion.summary ?? suggestionLabel(suggestion)}
        </div>
        <div className="mt-1 text-[11px] text-text-secondary">{suggestionLabel(suggestion)}</div>
      </div>
      <div className="flex shrink-0 flex-wrap gap-1.5">
        {suggestion.provider && <Badge variant="outline">{suggestion.provider}</Badge>}
        {suggestion.model && <Badge variant="secondary">{suggestion.model}</Badge>}
        {suggestion.status !== 'open' && <Badge variant="outline">{suggestion.status}</Badge>}
      </div>
    </div>
  );
}

export function markdownDiffPreview(
  suggestion: Pick<AiSuggestion, 'kind' | 'proposedMarkdown'>
): string {
  const line =
    suggestion.proposedMarkdown
      .split(/\r?\n/)
      .map(part => part.trim())
      .find(Boolean) ?? '(empty)';
  const prefix =
    suggestion.kind === 'suggest_replace' || suggestion.kind === 'generate_section' ? '~' : '+';
  return `${prefix} ${line}`;
}

function suggestionLabel(suggestion: AiSuggestion): string {
  switch (suggestion.kind) {
    case 'generate_full':
      return 'Generated document';
    case 'generate_section':
      return 'Generated section';
    case 'suggest_replace':
      return 'Replace suggestion';
    case 'suggest_insert':
      return 'Insert suggestion';
  }
}
